import * as ds from '@attain-apps/fe/web/main/lib/design'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import * as excel from '@attain-apps/fe/web/main/lib/excel'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import { Config } from './config'
import { Name } from './name'
import type { TypeParams } from './type-params'

/** @see {@link ec.Collection} */
export class Collection extends ec.Collection<TypeParams> {
  constructor(
    config: Config,
    injectableDependencies: ec.InjectableDependencies,
  ) {
    const { ref } = config
    super(
      {
        api: Config.getApi(config),
        context$: Config.getContext$(config),
        identify: row => {
          Collection.assertIsId(
            row.id.value,
            'Row ID must be a string or number',
          )

          return TypeChecker.isString(row.id.value)
            ? row.id.value
            : // Sometimes dms query returns a number as an id, so we need to cast
              // it because entityCollection only uses strings for ids.
              row.id.value.toString()
        },
        name: Name.from(ref),
        plugins: {
          additionalFactories: [
            _getTablePluginFactory(config),
            new excel.ec.Factory(),
          ],
          currentList: {
            quantity: {
              get: quantity => quantity ?? 20,
            },
          },
        },
      },
      injectableDependencies,
    )
  }
}
export namespace Collection {
  export const assertIsId: TypeAsserter<string | number> =
    TypeAsserter.Factory.from(
      TypeChecker.Factory.union(TypeChecker.isString, TypeChecker.isNumber),
    )
}

/** Maps dynamic table fields and row actions to canonical table configuration. */
function _getTablePluginFactory({
  actionHandlers,
}: Config): ds.table.ec.Factory<TypeParams> {
  return new ds.table.ec.Factory<TypeParams>({
    cellConfig: ({ attribute, entity }) => {
      const color = entity.fields?.[attribute.id]?.color

      return TypeChecker.isNotNullish(color) ? { css: { color } } : undefined
    },
    rowConfig: ({
      entity: { onClick, onDelete, onEdit, overflowActions, id },
    }) => ({
      ...(id.value === 'totalRow'
        ? {
            css: {
              borderTop: '1px solid black',
              bottom: '0',
              fontWeight: 'bold',
              position: 'sticky',
              width: '100%',
            },
          }
        : {}),
      iconsConfigs: [
        onEdit
          ? {
              class: 'atn-icon atn-edit atn-blue',
              onClick: () => {
                if (onEdit === 'auto') {
                  throw new Error(
                    'onEdit cannot be "auto" when using action handlers',
                  )
                }

                // TODO: In the future, this will be handled as an overflow
                // action, but to avoid technical debt, we'll implement it
                // directly now
                if (actionHandlers?.overflow) {
                  actionHandlers.overflow(onEdit).execute()
                }
              },
            }
          : undefined,
        onDelete
          ? {
              class: 'atn-icon atn-trash atn-red',
              onClick: () => {
                // TODO: In the future, this will be handled as an overflow
                // action, but to avoid technical debt, we'll implement it
                // directly now
                if (actionHandlers?.overflow) {
                  actionHandlers.overflow(onDelete).execute()
                }
              },
            }
          : undefined,
        ...(overflowActions
          ? overflowActions.map(actionRef => {
              if (!actionRef.icon) {
                console.warn('Overflow action icon is not defined', actionRef)
                return undefined
              }

              return {
                class: actionRef.icon,
                onClick: () => {
                  if (actionHandlers?.overflow) {
                    const handler = actionHandlers?.overflow(actionRef)
                    handler.execute()
                  }
                },
              }
            })
          : []),
      ].filter(TypeChecker.isNotNullish.raw),
      onClick: onClick
        ? () => {
            if (actionHandlers?.onClick) {
              const handler = actionHandlers.onClick(onClick)
              handler.execute()
            }
          }
        : undefined,
    }),
  })
}
